// ============================================
// Unified Login Page — Single login for all roles
// Admin/Staff → stays on Admin Portal
// Student/Parent → redirects to User Portal
// ============================================
import { useState, useEffect, useRef, type FormEvent } from 'react';
import './Login.css';
import { uploadUrl } from '../utils/uploadUrl';

interface LoginResponse {
  success: boolean;
  data?: {
    token: string;
    otp_required?: boolean;
    login_token?: string;
    channel?: 'sms' | 'email';
    target_masked?: string;
    user: {
      id: number;
      name: string;
      email?: string;
      role: string;
      image?: string;
    };
    permissions?: string[];  // e.g. ['student-can_view', 'fees-can_add']
    modules?: string[];       // e.g. ['student_information', 'fees_collection']
    settings: {
      schoolName: string;
      dateFormat: string;
      currencySymbol: string;
      timezone: string;
    };
  };
  error?: string;
}

// Only student/parent go to User Portal — all other roles (including custom ones
// like "Computer Operator", "Lab Assistant", "HOD" etc.) stay on Admin Portal.
const USER_ROLES = ['student', 'parent'];

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // OTP state
  const [otpRequired, setOtpRequired] = useState(false);
  const [loginToken, setLoginToken] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpChannel, setOtpChannel] = useState<'sms' | 'email'>('sms');
  const [targetMasked, setTargetMasked] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [schoolName, setSchoolName] = useState('');
  const [schoolLogo, setSchoolLogo] = useState('');
  const [schoolAddress, setSchoolAddress] = useState('');
  const [footerText, setFooterText] = useState('');

  // On mount, check if already logged in
  useEffect(() => {
    // Handle logout from user portal — clear admin localStorage
    const params = new URLSearchParams(window.location.search);
    if (params.get('logout') === '1') {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_role');
      localStorage.removeItem('auth_permissions');
      localStorage.removeItem('auth_modules');
      localStorage.removeItem('auth_settings');
      localStorage.removeItem('settings');
      localStorage.removeItem('auth_languages');
      localStorage.removeItem('auth_session');
      localStorage.removeItem('auth_campus');
      // Clean up URL
      window.history.replaceState(null, '', '/login');
      return; // Don't auto-redirect
    }

    const token = localStorage.getItem('auth_token');
    const role = localStorage.getItem('auth_role');
    if (token && role) {
      redirectByRole(role);
    }
    // Load remembered username
    const saved = localStorage.getItem('remembered_username');
    if (saved) {
      setUsername(saved);
      setRememberMe(true);
    }
    // Fetch school settings (name + logo + footer text) for login page display
    fetch('/api/admin/settings/public')
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) {
          if (d.data.name) setSchoolName(d.data.name);
          if (d.data.admin_logo) setSchoolLogo(d.data.admin_logo);
          if (d.data.address) setSchoolAddress(d.data.address);
          if (d.data.footer_text) setFooterText(d.data.footer_text);
        }
      })
      .catch(() => {});
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  function redirectByRole(role: string) {
    if (USER_ROLES.includes(role.toLowerCase())) {
      // Student/Parent → redirect to User Portal
      // Pass auth data via URL hash since localStorage is not shared across ports in dev
      const token = localStorage.getItem('auth_token') || '';
      const user = localStorage.getItem('auth_user') || '';
      const settings = localStorage.getItem('auth_settings') || '';
      const modules = localStorage.getItem('auth_modules') || '';
      const languages = localStorage.getItem('auth_languages') || '';
      const session = localStorage.getItem('auth_session') || '';
      const authData = encodeURIComponent(JSON.stringify({ token, user, settings, modules, languages, session, role }));
      // In production (same origin), use relative path; in dev, use localhost:5174
      const isDev = window.location.hostname === 'localhost' && window.location.port === '5173';
      const portalBase = isDev ? 'http://localhost:5174' : '';
      window.location.href = `${portalBase}/portal/dashboard#auth=${authData}`;
    } else {
      // Admin/Staff → stay on Admin Portal
      window.location.href = '/dashboard';
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Try admin login first
      let response = await fetch('/api/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      let data: LoginResponse = await response.json();

      // If admin login fails with 401, try user login
      if (!response.ok && response.status === 401) {
        response = await fetch('/api/user/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        data = await response.json();
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Invalid username or password');
      }

      if (data.data?.otp_required) {
        // Switch to OTP step
        setOtpRequired(true);
        setLoginToken(data.data.login_token || '');
        setOtpChannel(data.data.channel || 'sms');
        setTargetMasked(data.data.target_masked || '');
        setResendCooldown(30);
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError('');
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
        return;
      }

      // Save auth data
      localStorage.setItem('auth_token', data.data!.token);
      localStorage.setItem('auth_role', data.data!.user.role);
      // Store user with schoolName included
      const userToStore = {
        ...data.data!.user,
        schoolName: data.data!.settings?.schoolName || 'School',
      };
      localStorage.setItem('auth_user', JSON.stringify(userToStore));
      localStorage.setItem('auth_settings', JSON.stringify(data.data!.settings));
      localStorage.setItem('settings', JSON.stringify(data.data!.settings));
      // Store permissions and modules for RBAC sidebar filtering
      localStorage.setItem('auth_permissions', JSON.stringify(data.data!.permissions || []));
      localStorage.setItem('auth_modules', JSON.stringify(data.data!.modules || []));
      // Store enabled languages for language switcher (only DB-configured ones)
      localStorage.setItem('auth_languages', JSON.stringify((data.data as any)?.enabledLanguages || []));
      // Store session (academic year) data
      localStorage.setItem('auth_session', JSON.stringify((data.data as any)?.session || {}));

      // Handle "Remember me"
      if (rememberMe) {
        localStorage.setItem('remembered_username', username);
      } else {
        localStorage.removeItem('remembered_username');
      }

      // Redirect based on role
      redirectByRole(data.data!.user.role);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ─── OTP Input Handlers ───
  const handleOtpDigitChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return; // digits only
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    setOtpError('');
    // Auto-focus next
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 filled
    if (value && index === 5 && newDigits.every(d => d)) {
      handleVerifyOtp(newDigits.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      const code = otpDigits.join('');
      if (code.length === 6) handleVerifyOtp(code);
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setOtpDigits(newDigits);
      setOtpError('');
      otpRefs.current[5]?.focus();
      handleVerifyOtp(pasted);
    }
  };

  const handleVerifyOtp = async (code?: string) => {
    const otpCode = code || otpDigits.join('');
    if (otpCode.length !== 6) { setOtpError('Please enter all 6 digits'); return; }
    setVerifying(true);
    setOtpError('');
    try {
      const res = await fetch('/api/admin/auth/login/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_token: loginToken, otp_code: otpCode }),
      });
      const data = await res.json();
      if (data.success && data.data?.token) {
        localStorage.setItem('auth_token', data.data.token);
        localStorage.setItem('auth_user', JSON.stringify(data.data.user));
        localStorage.setItem('auth_role', data.data.user?.role || '');
        if (data.data.permissions) localStorage.setItem('auth_permissions', JSON.stringify(data.data.permissions));
        if (data.data.modules) localStorage.setItem('auth_modules', JSON.stringify(data.data.modules));
        if (data.data.settings) localStorage.setItem('auth_settings', JSON.stringify(data.data.settings));
        if (data.data.settings) localStorage.setItem('settings', JSON.stringify(data.data.settings));
        if (data.data.enabledLanguages) localStorage.setItem('auth_languages', JSON.stringify(data.data.enabledLanguages));
        if (data.data.session) localStorage.setItem('auth_session', JSON.stringify(data.data.session));
        redirectByRole(data.data.user?.role || 'admin');
      } else {
        setOtpError(data.error || 'Invalid OTP');
        setOtpDigits(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch {
      setOtpError('Connection failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setOtpError('');
    try {
      const res = await fetch('/api/admin/auth/login/resend-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login_token: loginToken }),
      });
      const data = await res.json();
      if (data.success) {
        setLoginToken(data.data.login_token);
        setOtpChannel(data.data.channel);
        setTargetMasked(data.data.target_masked);
        setResendCooldown(data.data.cooldown || 30);
        setOtpDigits(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      } else {
        setOtpError(data.error || 'Failed to resend OTP');
      }
    } catch {
      setOtpError('Connection failed. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setOtpRequired(false);
    setLoginToken('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setResendCooldown(0);
  };

  return (
    <div className="login-page">
      {/* Animated background shapes */}
      <div className="login-bg-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="login-container">
        {/* Left side — branding */}
        <div className="login-brand">
          <div className="brand-content">
            {schoolLogo ? (
              <img src={uploadUrl(schoolLogo, 'school_content/admin_logo')} alt={schoolName} className="brand-logo-img" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <div className="brand-icon">🎓</div>
            )}
            <h1>{schoolName}</h1>
            {schoolAddress && (
              <p style={{ fontSize: '0.85rem', opacity: 0.8, marginTop: 6, lineHeight: 1.5, maxWidth: 340, textAlign: 'center' }}>{schoolAddress}</p>
            )}
          </div>
        </div>

        {/* Right side — login form */}
        <div className="login-form-section">
          <div className="login-form-card">
            {otpRequired ? (
              <div className="otp-step">
                <div className="otp-step__icon">🔐</div>
                <h2 className="otp-step__title">OTP Verification</h2>
                <p className="otp-step__subtitle">A 6-digit code has been sent to your</p>
                <div className="otp-step__channel">
                  {otpChannel === 'sms' ? '📱' : '📧'}
                  {otpChannel === 'sms' ? 'SMS' : 'Email'}: {targetMasked}
                </div>

                <div className="otp-inputs" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => { otpRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className={`otp-digit${digit ? ' otp-digit--filled' : ''}${otpError ? ' otp-digit--error' : ''}`}
                      value={digit}
                      onChange={e => handleOtpDigitChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>

                <p className="otp-error">{otpError || '\u00A0'}</p>

                <button
                  type="button"
                  className="login-btn otp-verify-btn"
                  onClick={() => handleVerifyOtp()}
                  disabled={verifying || otpDigits.join('').length !== 6}
                >
                  {verifying ? (
                    <><span className="spinner"></span>Verifying...</>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>

                <div className="otp-actions">
                  <button
                    type="button"
                    className="otp-resend"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || resending}
                  >
                    {resending ? 'Sending...' : resendCooldown > 0
                      ? `Resend OTP (${resendCooldown}s)`
                      : "Didn't receive? Resend OTP"}
                  </button>
                  <button type="button" className="otp-back" onClick={handleBackToLogin}>
                    ← Back to Login
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="form-header">
                  <h2>Welcome Back</h2>
                  <p>Sign in to your account</p>
                </div>

                {error && (
                  <div className="login-error" id="login-error">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="8" fill="currentColor" opacity="0.15"/>
                      <path d="M8 4v4M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} id="login-form">
                  <div className="form-group">
                    <label htmlFor="username">Username or Email</label>
                    <div className="input-wrapper">
                      <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter your username or email"
                        autoComplete="username"
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="input-wrapper">
                      <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="toggle-password"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="form-options">
                    <label className="checkbox-wrapper" htmlFor="remember-me">
                      <input
                        type="checkbox"
                        id="remember-me"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      Remember me
                    </label>
                    <a href="/forgot-password" className="forgot-link">Forgot password?</a>
                  </div>

                  <button
                    type="submit"
                    className="login-btn"
                    id="login-submit"
                    disabled={loading || !username || !password}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

                <div className="login-footer">
                  {footerText ? (
                    <p>{footerText}</p>
                  ) : schoolName ? (
                    <p>Powered by <strong>{schoolName}</strong></p>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
